import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {Store} from "@ngrx/store";
import {FormBuilder} from "@angular/forms";
import {
    selectAssetFinancialHistoryByPeriods,
    selectAssetFinancialHistoryEdit,
    selectBaselineAssetFinancialHistory,
    selectIsAnyAssetFormInEditMode,
    selectRecentFinancialPerformance,
    selectRecentFinancialPerformanceEdit,
    selectAssetCommentaryHistory,
    selectRecentFinancialPerformanceSaveState,
    selectRelatedReportId,
    selectReportDate,
    selectSelectedAssetCurrencyIsoCode
} from "../../store/asset/asset.selectors";
import {map, switchMap} from "rxjs/operators";
import {FinancialPerformanceRecord} from "../../models/asset/financial-performance-record";
import {BehaviorSubject, combineLatest, distinctUntilChanged, Subscription} from "rxjs";
import {AssetFinancialHistoryActions, RecentFinancialPerformanceActions} from "../../store/asset.actions";
import {PeriodType} from "../../../shared/model/traceable";

function sameArray(a: readonly PeriodType[], b: readonly PeriodType[]) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
}

@Component({
    selector: "valumize-asset-fin-data-history",
    templateUrl: "./asset-fin-data-history.component.html",
    styleUrls: ["./asset-fin-data-history.component.scss"],
    standalone: false
})
export class AssetFinDataHistoryComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);

    selectedAssetFinancialHistory$ = this.store.select(selectAssetFinancialHistoryByPeriods(["QUARTER", "LAST_TWELVE_MONTHS"]));
    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    companyCurrencyIso$ = this.store.select(selectSelectedAssetCurrencyIsoCode);
    relatedReportId$ = this.store.select(selectRelatedReportId);
    // The current report's date, so the editable entry can label itself without depending on
    // the history read-model having loaded.
    reportDate$ = this.store.select(selectReportDate);
    recentFinancialPerformance$ = this.store.select(selectRecentFinancialPerformance);
    // Shared with the developments-since-investment card: one request, each card reads its own field.
    commentaryHistory$ = this.store.select(selectAssetCommentaryHistory);
    // This editor's own save state — the tab used to render no feedback for its own save at all.
    saveState$ = this.store.select(selectRecentFinancialPerformanceSaveState);

    selectedAssetFinancialHistoryData: FinancialPerformanceRecord[] = [];
    baselineAssetFinancialHistoryData: FinancialPerformanceRecord[] = [];
    isEditable = false;
    isDataset = false;
    recentFinancialPerformanceIsEditable = false;
    // What the server holds, so leaving edit mode can put the form back.
    private persistedRecentFinancialPerformanceText = "";
    subscriptions: Subscription[] = [];

    private readonly periodTypes$ = new BehaviorSubject<PeriodType[]>([]);
    private readonly showGpProjected$ = new BehaviorSubject<boolean>(false);

    recentFinancialPerformanceForm = this.formBuilder.group({
        text: this.formBuilder.control("", {nonNullable: true})
    });

    ngOnInit(): void {

        this.subscriptions.push(
            combineLatest([
                this.periodTypes$.pipe(distinctUntilChanged(sameArray)),
                this.showGpProjected$.pipe(distinctUntilChanged())
            ]).pipe(
                switchMap(([types, showGpProjected]) =>
                    this.store.select(selectAssetFinancialHistoryByPeriods(types, showGpProjected))
                )
            ).subscribe(api => {
                this.selectedAssetFinancialHistoryData = api.data;
            }));

        this.subscriptions.push(
            this.store.select(selectAssetFinancialHistoryEdit).pipe(map((isEditable) => {
                this.isEditable = isEditable;
            })).subscribe());

        this.subscriptions.push(
            this.store.select(selectBaselineAssetFinancialHistory).pipe(map((baselineAssetFinancialHistory) => {
                if (baselineAssetFinancialHistory.status === "LOADED" && baselineAssetFinancialHistory.data.length > 0) {
                    this.isDataset = true;
                    this.baselineAssetFinancialHistoryData = baselineAssetFinancialHistory.data;
                } else {
                    this.isDataset = false;
                }
            })).subscribe());

        this.subscriptions.push(
            this.store.select(selectRecentFinancialPerformanceEdit).pipe(map((isEditable) => {
                this.recentFinancialPerformanceIsEditable = isEditable;
                if (isEditable) {
                    this.recentFinancialPerformanceForm.enable();
                } else {
                    // Cancel only flips this flag and never touches generalPartnerValuation.data, so
                    // the store emits nothing and the patch subscription below cannot fire. Restore
                    // the persisted text here or the abandoned draft survives, invisibly, and the
                    // next Save writes it.
                    this.recentFinancialPerformanceForm.patchValue({text: this.persistedRecentFinancialPerformanceText});
                    this.recentFinancialPerformanceForm.disable();
                }
            })).subscribe());

        this.subscriptions.push(
            this.recentFinancialPerformance$.pipe(
                map((recentFinancialPerformance) => {
                    // Track unconditionally so cancel always has the persisted text to restore.
                    this.persistedRecentFinancialPerformanceText = recentFinancialPerformance.text ?? "";

                    if (this.recentFinancialPerformanceIsEditable) {
                        return;
                    }
                    this.recentFinancialPerformanceForm.patchValue({text: this.persistedRecentFinancialPerformanceText});
                })
            ).subscribe());
    }

    onSave(assetHistoryToSave: FinancialPerformanceRecord[]) {
        this.store.dispatch(AssetFinancialHistoryActions.save({financialHistory: assetHistoryToSave}));
    }

    onEdit() {
        this.store.dispatch(AssetFinancialHistoryActions.edit());
    }

    onCancel() {
        this.store.dispatch(AssetFinancialHistoryActions.cancel());
    }

    onCalc(updatedRecords: FinancialPerformanceRecord[]) {
        this.store.dispatch(AssetFinancialHistoryActions.calc({financialHistoryRecord: updatedRecords}));
    }

    onPeriodTypesChange(types: PeriodType[]) {
        this.periodTypes$.next(types);
    }

    onGpProjectedFilterChange(showGpProjected: boolean) {
        this.showGpProjected$.next(showGpProjected);
    }

    editRecentFinancialPerformance = () => this.store.dispatch(RecentFinancialPerformanceActions.edit());

    saveRecentFinancialPerformance() {
        if (this.recentFinancialPerformanceForm.valid) {
            this.store.dispatch(RecentFinancialPerformanceActions.save(this.recentFinancialPerformanceForm.getRawValue()));
        }
    }

    cancelRecentFinancialPerformance = () => this.store.dispatch(RecentFinancialPerformanceActions.cancel());

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
